import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { NextStep } from '../../assignment-view.helpers';
import { MbaIcon, MbaIconName } from '../../atoms/mba-icon/mba-icon';
import { ui } from '../../assignment-ui';

const ACTION_ICON: Record<NextStep['action'], MbaIconName> = {
  remind: 'bell',
  hire: 'user-check',
  view: 'arrow-right',
};

/**
 * One next-step button (plus Decline for applicants) and the ⋯ menu. Shared by the
 * table, the mobile cards and the side panel so they always offer the same action.
 */
@Component({
  selector: 'mbt-next-step-cell',
  imports: [MbaIcon],
  template: `
    <div class="flex items-center justify-end gap-1.5" data-row-click-ignore>
      @if (busy()) {
        <span class="inline-block size-5 animate-spin rounded-full border-2 border-(--border-subtle) border-t-(--app-accent)"
          role="status" aria-label="Working"></span>
      } @else {
        <button type="button" [class]="step().primary ? ui.btnPrimary : ui.btnOutline" [disabled]="disabled()" (click)="primary.emit()">
          <mba-icon [name]="icon(step().action)" />{{ step().label }}
          @if (step().action === 'hire' && price()) {
            <span class="tabular-nums">· {{ price() }}</span>
          }
        </button>
        @if (step().decline) {
          <button type="button" [class]="ui.btnOutline" [disabled]="disabled()" (click)="decline.emit()">Decline</button>
        }
      }
      @if (showMenu()) {
        <div class="relative">
          <button #trigger type="button" aria-haspopup="menu" [attr.aria-expanded]="open()" aria-label="More actions"
            class="grid size-8 cursor-pointer place-items-center rounded-[9px] text-(--fg-2) hover:bg-(--bg-alt) hover:text-(--fg-1) focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-(--app-accent-ring)"
            [class]="open() ? 'bg-(--bg-alt)' : ''" (click)="toggle($event)"><mba-icon name="more" /></button>
          @if (open()) {
            <div role="menu" [style.top.px]="popTop()" [style.right.px]="popRight()"
              class="fixed z-40 flex min-w-[210px] flex-col rounded-lg border border-(--border-subtle) bg-(--mba-surface) p-[5px] text-left shadow-[0_8px_24px_rgba(18,6,43,0.1)] animate-[mba-in_140ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none">
              <button type="button" role="menuitem" [class]="menuItem" (click)="pick('details')">
                <mba-icon name="panel-right-open" size="size-[15px] text-(--fg-3)" />Open details</button>
              @if (canWithdraw()) {
                <hr class="my-1 border-0 border-t border-(--border-subtle)" />
                <button type="button" role="menuitem" [class]="menuItemDanger" (click)="pick('withdraw')">
                  <mba-icon name="x-circle" size="size-[15px]" />Withdraw invite</button>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
  host: {
    style: 'display: contents',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'setOpen(false)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NextStepCell {
  protected readonly ui = ui;
  private readonly menuItemBase =
    'flex cursor-pointer items-center gap-[9px] whitespace-nowrap rounded-[7px] px-2.5 py-2 text-[13px] font-semibold hover:bg-(--bg-alt) focus-visible:bg-(--bg-alt) focus-visible:outline-none';
  protected readonly menuItem = `${this.menuItemBase} text-(--fg-1)`;
  protected readonly menuItemDanger = `${this.menuItemBase} text-(--danger)`;
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  readonly step = input.required<NextStep>();
  readonly busy = input(false);
  /** Expired applications keep the menu but can't be acted on. */
  readonly disabled = input(false);
  readonly showMenu = input(true);
  readonly canWithdraw = input(false);
  /** Formatted hire cost shown on the Hire button; empty while it's still being priced. */
  readonly price = input('');

  readonly primary = output<void>();
  readonly decline = output<void>();
  readonly openDetails = output<void>();
  readonly withdraw = output<void>();

  protected readonly open = signal(false);
  /**
   * The popover is fixed to the viewport under its trigger rather than placed in the cell:
   * the table's scroll wrapper would clip it, and each sticky next-step cell is its own
   * stacking context, so the next row would paint over it. Scrolling closes it.
   */
  protected readonly popTop = signal(0);
  protected readonly popRight = signal(0);
  private readonly closeOnScroll = () => this.open.set(false);

  constructor() {
    inject(DestroyRef).onDestroy(() => document.removeEventListener('scroll', this.closeOnScroll, true));
  }

  protected icon(action: NextStep['action']): MbaIconName {
    return ACTION_ICON[action];
  }

  protected toggle(event: Event): void {
    event.stopPropagation();
    if (this.open()) {
      this.setOpen(false);
      return;
    }
    const button = this.trigger()?.nativeElement;
    if (button) {
      const b = button.getBoundingClientRect();
      this.popTop.set(b.bottom + 6);
      this.popRight.set(document.documentElement.clientWidth - b.right);
    }
    this.setOpen(true);
  }

  protected setOpen(value: boolean): void {
    this.open.set(value);
    if (value) {
      document.addEventListener('scroll', this.closeOnScroll, true);
    } else {
      document.removeEventListener('scroll', this.closeOnScroll, true);
    }
  }

  protected pick(item: 'details' | 'withdraw'): void {
    this.setOpen(false);
    if (item === 'details') {
      this.openDetails.emit();
    } else {
      this.withdraw.emit();
    }
  }

  protected onDocumentClick(event: Event): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.setOpen(false);
    }
  }
}
